<template>
  <div class="rte" :class="{ 'rte--invalid': invalid, 'rte--slot': Boolean(slotFormat) }">
    <div v-if="editor" class="rte__toolbar">
      <button type="button" class="rte__tb-btn" title="Undo" @click="chain().undo().run()">
        <i class="pi pi-replay" aria-hidden="true"></i>
      </button>
      <button type="button" class="rte__tb-btn" title="Redo" @click="chain().redo().run()">
        <i class="pi pi-refresh" aria-hidden="true"></i>
      </button>

      <span class="rte__tb-divider" aria-hidden="true"></span>

      <select class="rte__tb-select rte__tb-select--zoom" aria-label="Zoom" v-model.number="zoom">
        <option v-for="z in zoomOptions" :key="z" :value="z">{{ z }}%</option>
      </select>

      <select
        class="rte__tb-select"
        aria-label="Font family"
        :value="currentFontFamily"
        @change="setFontFamily($event.target.value)"
      >
        <option value="">Font</option>
        <option v-for="font in fontFamilyOptions" :key="font.value" :value="font.value">
          {{ font.label }}
        </option>
      </select>

      <select
        class="rte__tb-select rte__tb-select--small"
        aria-label="Font size"
        :value="currentFontSize"
        @change="setFontSize($event.target.value)"
      >
        <option value="">Size</option>
        <option v-for="size in fontSizeSelectOptions" :key="size" :value="String(size)">
          {{ size }}
        </option>
      </select>

      <span class="rte__tb-divider" aria-hidden="true"></span>

      <button
        type="button"
        class="rte__tb-btn rte__tb-btn--bold"
        :class="markButtonClass('bold')"
        aria-label="Bold"
        :title="buttonTitle('Bold', isMarkInherited('bold'))"
        @click="chain().toggleBold().run()"
      >B</button>
      <button
        type="button"
        class="rte__tb-btn rte__tb-btn--italic"
        :class="markButtonClass('italic')"
        aria-label="Italic"
        :title="buttonTitle('Italic', isMarkInherited('italic'))"
        @click="chain().toggleItalic().run()"
      >I</button>
      <button
        type="button"
        class="rte__tb-btn rte__tb-btn--underline"
        :class="markButtonClass('underline')"
        aria-label="Underline"
        :title="buttonTitle('Underline', isMarkInherited('underline'))"
        @click="chain().toggleUnderline().run()"
      >U</button>
      <button
        type="button"
        class="rte__tb-btn rte__tb-btn--strike"
        :class="markButtonClass('strike')"
        aria-label="Strikethrough"
        :title="buttonTitle('Strikethrough', isMarkInherited('strike'))"
        @click="chain().toggleStrike().run()"
      >S</button>

      <span class="rte__tb-divider" aria-hidden="true"></span>

      <label class="rte__tb-btn" title="Highlight">
        <span class="rte__tb-swatch" :style="{ backgroundColor: currentHighlight }"></span>
        <input
          type="color"
          class="rte__tb-color-input"
          :value="currentHighlight"
          @input="setHighlight($event.target.value)"
        />
      </label>
      <label class="rte__tb-btn rte__tb-btn--text-color" title="Text color">
        <span>A</span>
        <span class="rte__tb-color-bar" :style="{ backgroundColor: currentColor }"></span>
        <input
          type="color"
          class="rte__tb-color-input"
          :value="currentColor"
          @input="setColor($event.target.value)"
        />
      </label>

      <span class="rte__tb-divider" aria-hidden="true"></span>

      <button
        type="button"
        class="rte__tb-btn"
        :class="{ 'is-active': editor.isActive('link') }"
        title="Link"
        @click="toggleLink()"
      >
        <i class="pi pi-link" aria-hidden="true"></i>
      </button>
      <button type="button" class="rte__tb-btn" title="Image" @click="addImage()">
        <i class="pi pi-image" aria-hidden="true"></i>
      </button>

      <span class="rte__tb-divider" aria-hidden="true"></span>

      <button
        type="button"
        class="rte__tb-btn"
        :class="{ 'is-active': editor.isActive('bulletList') }"
        title="Bullet list"
        @click="chain().toggleBulletList().run()"
      >
        <i class="pi pi-list" aria-hidden="true"></i>
      </button>
      <button
        type="button"
        class="rte__tb-btn"
        :class="{ 'is-active': editor.isActive('orderedList') }"
        title="Numbered list"
        @click="chain().toggleOrderedList().run()"
      >
        <i class="pi pi-sort-numeric-down" aria-hidden="true"></i>
      </button>

      <span class="rte__tb-divider" aria-hidden="true"></span>

      <button
        type="button"
        class="rte__tb-btn"
        :class="alignButtonClass('left')"
        aria-label="Align left"
        :title="buttonTitle('Align left', isAlignInherited('left'))"
        @click="chain().setTextAlign('left').run()"
      >
        <i class="pi pi-align-left" aria-hidden="true"></i>
      </button>
      <button
        type="button"
        class="rte__tb-btn"
        :class="alignButtonClass('center')"
        aria-label="Align center"
        :title="buttonTitle('Align center', isAlignInherited('center'))"
        @click="chain().setTextAlign('center').run()"
      >
        <i class="pi pi-align-center" aria-hidden="true"></i>
      </button>
      <button
        type="button"
        class="rte__tb-btn"
        :class="alignButtonClass('right')"
        aria-label="Align right"
        :title="buttonTitle('Align right', isAlignInherited('right'))"
        @click="chain().setTextAlign('right').run()"
      >
        <i class="pi pi-align-right" aria-hidden="true"></i>
      </button>
      <button
        type="button"
        class="rte__tb-btn"
        :class="alignButtonClass('justify')"
        aria-label="Justify"
        :title="buttonTitle('Justify', isAlignInherited('justify'))"
        @click="chain().setTextAlign('justify').run()"
      >
        <i class="pi pi-align-justify" aria-hidden="true"></i>
      </button>

      <select
        class="rte__tb-select rte__tb-select--small"
        aria-label="Line spacing"
        :value="currentLineHeight"
        @change="setLineHeight($event.target.value)"
      >
        <option value="">Line</option>
        <option v-for="lineHeight in lineHeightOptions" :key="lineHeight" :value="String(lineHeight)">
          {{ lineHeight }}
        </option>
      </select>

      <button
        type="button"
        class="rte__tb-btn"
        :class="directionButtonClass"
        aria-label="Right to left"
        :title="buttonTitle('Right to left', directionButtonClass['is-inherited'])"
        @click="toggleRtl()"
      >RTL</button>

      <button type="button" class="rte__tb-btn" title="Clear formatting" @click="clearFormat()">
        <i class="pi pi-eraser" aria-hidden="true"></i>
      </button>

      <template v-if="variables.length || variableAction">
        <div class="rte__tb-spacer"></div>

        <div class="rte-variable">
          <button
            ref="variableButton"
            type="button"
            class="rte-variable__btn"
            @click="toggleVariableMenu()"
          >
            <span>Variable</span>
            <i class="pi pi-chevron-down" aria-hidden="true"></i>
          </button>
          <Teleport to="body">
            <div
              v-if="showVariableMenu"
              ref="variableMenu"
              class="rte-variable__menu"
              :class="{ 'rte-variable__menu--rich': variableMenuIsRich }"
              :style="variableMenuStyle"
            >
              <button
                v-for="variable in variables"
                :key="variable.value"
                type="button"
                class="rte-variable__item"
                @click="insertVariable(variable.value)"
              >
                <span class="rte-variable__label">{{ variable.label }}</span>
                <span v-if="variable.required" class="rte-variable__required">Required</span>
              </button>
              <template v-if="variableAction">
                <div v-if="variables.length" class="rte-variable__divider" aria-hidden="true"></div>
                <button type="button" class="rte-variable__action" @click="runVariableAction()">
                  <i class="pi" :class="variableAction.icon || 'pi-plus'" aria-hidden="true"></i>
                  <span>{{ variableAction.label }}</span>
                </button>
              </template>
            </div>
          </Teleport>
        </div>
      </template>
    </div>

    <div class="rte__canvas" :style="{ zoom: String(zoom / 100) }">
      <EditorContent class="rte__editor" :editor="editor" :style="editorStyle" />
    </div>
  </div>
</template>

<script>
import { markRaw } from 'vue';
import { Editor, EditorContent } from '@tiptap/vue-3';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import TextStyle from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import FontFamily from '@tiptap/extension-font-family';
import { Extension } from '@tiptap/core';
import {
  MINYANIM_ROLL_LINE_HEIGHT_OPTIONS,
  MINYANIM_TEXT_EDITOR_FONT_OPTIONS,
  MINYANIM_TEXT_EDITOR_ZOOM_OPTIONS,
  ZMANIM_N_FONT_SIZE_OPTIONS,
} from '@/utils/text_editor_options';
import {
  storedCustomFonts,
  withCustomFontStackOptions,
} from '@/utils/custom_fonts';
import {
  TemplateSlot,
  setTemplateSlotFormat,
  templateSlotDefaults,
} from '@/utils/rich_template_slot_editor';

const FontSize = Extension.create({
  name: 'fontSize',
  addOptions() {
    return {
      types: ['textStyle'],
    };
  },
  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          fontSize: {
            default: null,
            parseHTML: (element) => {
              const value = element.style.fontSize || '';
              if (!value) return null;
              return value.endsWith('px') ? value.replace('px', '') : value;
            },
            renderHTML: (attributes) => {
              if (!attributes.fontSize) return {};
              const value = String(attributes.fontSize);
              const fontSize = /^\d+(\.\d+)?$/.test(value) ? `${value}px` : value;
              return {
                style: `font-size: ${fontSize}`,
              };
            },
          },
        },
      },
    ];
  },
  addCommands() {
    return {
      setFontSize:
        (fontSize) =>
        ({ chain }) => chain().setMark('textStyle', { fontSize }).run(),
      unsetFontSize:
        () =>
        ({ chain }) => chain().setMark('textStyle', { fontSize: null }).removeEmptyTextStyle().run(),
    };
  },
});

const LineHeight = Extension.create({
  name: 'lineHeight',
  addOptions() {
    return {
      types: ['paragraph', 'heading'],
    };
  },
  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          lineHeight: {
            default: null,
            parseHTML: (element) => element.style.lineHeight || null,
            renderHTML: (attributes) => {
              if (!attributes.lineHeight) return {};
              return {
                style: `line-height: ${attributes.lineHeight}`,
              };
            },
          },
        },
      },
    ];
  },
  addCommands() {
    return {
      setLineHeight:
        (lineHeight) =>
        ({ commands }) => this.options.types.some((type) => commands.updateAttributes(type, { lineHeight })),
      unsetLineHeight:
        () =>
        ({ commands }) => this.options.types.some((type) => commands.updateAttributes(type, { lineHeight: null })),
    };
  },
});

const TextDirection = Extension.create({
  name: 'textDirection',
  addOptions() {
    return {
      types: ['paragraph', 'heading'],
    };
  },
  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          textDirection: {
            default: null,
            parseHTML: (element) => element.getAttribute('dir') || null,
            renderHTML: (attributes) => {
              if (!attributes.textDirection) return {};
              return {
                dir: attributes.textDirection,
              };
            },
          },
        },
      },
    ];
  },
  addCommands() {
    return {
      setTextDirection:
        (textDirection) =>
        ({ commands }) => this.options.types.some((type) => commands.updateAttributes(type, { textDirection })),
      unsetTextDirection:
        () =>
        ({ commands }) => this.options.types.some((type) => commands.updateAttributes(type, { textDirection: null })),
    };
  },
});

// Empty Hebrew-friendly starting paragraph (RTL, centered) so an empty editor
// matches the authoring direction used across the app.
const DEFAULT_EDITOR_CONTENT = '<p dir="rtl" style="text-align: center"></p>';

// A font stack's first face, which is what tells two stacks apart.
const firstFontFamily = (stack) => String(stack || '').split(',')[0].trim().replace(/^['"]|['"]$/g, '');

const htmlHasText = (html) =>
  String(html || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim().length > 0;

export default {
  name: 'Rich_Text_Editor',
  components: {
    EditorContent,
  },
  props: {
    modelValue: {
      type: String,
      default: '',
    },
    // Renders the editor with a danger border (e.g. failed validation).
    invalid: {
      type: Boolean,
      default: false,
    },
    // Height of the editable area.
    height: {
      type: String,
      default: '320px',
    },
    // The Variable menu: { value, label }, and `required: true` to tag an
    // entry Required.
    variables: {
      type: Array,
      default: () => [],
    },
    // An entry at the foot of the Variable menu — { label, icon } — that emits
    // `variable-action` (the donation templates' "New field").
    variableAction: {
      type: Object,
      default: null,
    },
    // The formatting a template gives the content being written here
    // (utils/rich_template_slot: readRichSlotFormat). The content is drawn in
    // it — font, size, colour, alignment — the way the screen will show it,
    // and the toolbar reads it where the text sets nothing of its own. Only
    // drawn, never written into the content. null: no template.
    slotFormat: {
      type: Object,
      default: null,
    },
  },
  emits: ['update:modelValue', 'variable-action'],
  data() {
    return {
      editor: null,
      fontSizeOptions: ZMANIM_N_FONT_SIZE_OPTIONS,
      lineHeightOptions: MINYANIM_ROLL_LINE_HEIGHT_OPTIONS,
      zoomOptions: MINYANIM_TEXT_EDITOR_ZOOM_OPTIONS,
      zoom: 100,
      toolbarStateKey: 0,
      showVariableMenu: false,
      variableMenuStyle: {
        top: '0px',
        left: '0px',
        visibility: 'hidden',
      },
    };
  },
  computed: {
    // Built-in Hebrew faces plus whatever the organization uploaded in
    // Settings > Custom fonts. The store registers every uploaded face with the
    // browser as the library loads, so a picked one renders here immediately.
    fontFamilies() {
      return withCustomFontStackOptions(
        MINYANIM_TEXT_EDITOR_FONT_OPTIONS,
        storedCustomFonts(this.$store)
      );
    },
    editorStyle() {
      return {
        '--rte-height': this.height,
      };
    },
    // An action or Required tags need a wider menu than plain entries.
    variableMenuIsRich() {
      return Boolean(this.variableAction) || this.variables.some((variable) => variable.required);
    },
    // What the text looks like where it sets nothing itself: the template's
    // formatting, or nothing without one.
    slotDefaults() {
      return templateSlotDefaults(this.slotFormat);
    },
    // --- Tiptap toolbar reactive state ---
    // The text's own font, else the template's — as the menu lists it, even
    // when written as a different stack of the same face.
    currentFontFamily() {
      this.toolbarStateKey;
      const value = this.editor?.getAttributes('textStyle').fontFamily || this.slotDefaults.fontFamily || '';
      if (!value) return '';
      const face = firstFontFamily(value).toLowerCase();
      const option =
        this.fontFamilies.find((font) => font.value === value) ||
        this.fontFamilies.find((font) => firstFontFamily(font.value).toLowerCase() === face);
      return option ? option.value : value;
    },
    // The menu, plus the font in use when the menu does not list it, so the
    // menu names it instead of going blank.
    fontFamilyOptions() {
      const current = this.currentFontFamily;
      if (!current || this.fontFamilies.some((font) => font.value === current)) return this.fontFamilies;
      return [{ value: current, label: firstFontFamily(current) }, ...this.fontFamilies];
    },
    currentFontSize() {
      this.toolbarStateKey;
      const fontSize = this.editor?.getAttributes('textStyle').fontSize;
      return fontSize ? String(fontSize).replace('px', '') : this.slotDefaults.fontSize;
    },
    // The size menu, plus the size in use when the menu does not list it.
    fontSizeSelectOptions() {
      const current = Number(this.currentFontSize);
      if (
        !this.currentFontSize ||
        !Number.isFinite(current) ||
        this.fontSizeOptions.some((size) => Number(size) === current)
      ) {
        return this.fontSizeOptions;
      }
      return [...this.fontSizeOptions, current].sort((a, b) => a - b);
    },
    currentLineHeight() {
      this.toolbarStateKey;
      return (
        this.editor?.getAttributes('paragraph').lineHeight ||
        this.editor?.getAttributes('heading').lineHeight ||
        this.slotDefaults.lineHeight ||
        ''
      );
    },
    currentDirection() {
      this.toolbarStateKey;
      return (
        this.editor?.getAttributes('paragraph').textDirection ||
        this.editor?.getAttributes('heading').textDirection ||
        this.slotDefaults.direction ||
        ''
      );
    },
    isRtlActive() {
      return this.currentDirection === 'rtl';
    },
    // The paragraph's own direction lights the button; the template's, when
    // the paragraph sets none, shows as inherited.
    directionButtonClass() {
      this.toolbarStateKey;
      const own =
        this.editor?.getAttributes('paragraph').textDirection ||
        this.editor?.getAttributes('heading').textDirection ||
        '';
      return {
        'is-active': own === 'rtl',
        'is-inherited': !own && this.slotDefaults.direction === 'rtl',
      };
    },
    currentColor() {
      this.toolbarStateKey;
      return this.editor?.getAttributes('textStyle').color || this.slotDefaults.color || '#111827';
    },
    currentHighlight() {
      this.toolbarStateKey;
      return this.editor?.getAttributes('highlight').color || this.slotDefaults.highlight || '#4f46e5';
    },
  },
  watch: {
    modelValue(value) {
      if (!this.editor) return;
      // Avoid resetting the cursor/selection when the change originated here.
      const isSame = (value || '') === this.editor.getHTML();
      if (isSame) return;
      this.editor.commands.setContent(value || '', false);
    },
    slotFormat(format, previous) {
      if (!this.editor) return;
      setTemplateSlotFormat(this.editor, format);
      // Nothing written yet: start over from a bare paragraph, which takes the
      // template's alignment and direction — or, with the template taken
      // away, from the editor's usual RTL, centred one. The prop is checked
      // too, so content arriving in the same tick (an edit page loading) is
      // never overwritten.
      if (Boolean(format) !== Boolean(previous) && !htmlHasText(this.modelValue) && this.isBlank()) {
        this.editor.commands.setContent(format ? '' : DEFAULT_EDITOR_CONTENT, false);
        const html = this.editor.getHTML();
        if (this.modelValue && this.modelValue !== html) this.$emit('update:modelValue', html);
      }
      this.touchToolbarState();
    },
  },
  methods: {
    chain() {
      return this.editor.chain().focus();
    },
    // A single empty paragraph: nothing typed, nothing inserted.
    isBlank() {
      const doc = this.editor?.state.doc;
      return Boolean(doc && doc.childCount === 1 && doc.firstChild.isTextblock && doc.firstChild.content.size === 0);
    },
    // A button is lit for what the text itself has, and marked as inherited
    // for what only the template gives it: a click adds or removes the text's
    // own formatting, never the template's, so the two must not look alike.
    buttonTitle(label, inherited) {
      return inherited ? `${label} (set by the template)` : label;
    },
    // Bold / italic / underline / strike set on the text.
    isMarkActive(name) {
      this.toolbarStateKey;
      return Boolean(this.editor && this.editor.isActive(name));
    },
    // ...or given by the template alone.
    isMarkInherited(name) {
      this.toolbarStateKey;
      return Boolean(this.editor) && !this.editor.isActive(name) && Boolean(this.slotDefaults[name]);
    },
    markButtonClass(name) {
      return { 'is-active': this.isMarkActive(name), 'is-inherited': this.isMarkInherited(name) };
    },
    // The paragraph's own alignment.
    isAlignActive(align) {
      this.toolbarStateKey;
      return Boolean(this.editor && this.editor.isActive({ textAlign: align }));
    },
    // ...or, where it sets none, the template's.
    isAlignInherited(align) {
      this.toolbarStateKey;
      if (!this.editor) return false;
      const own =
        this.editor.getAttributes('paragraph').textAlign || this.editor.getAttributes('heading').textAlign;
      return !own && this.slotDefaults.textAlign === align;
    },
    alignButtonClass(align) {
      return { 'is-active': this.isAlignActive(align), 'is-inherited': this.isAlignInherited(align) };
    },
    setFontFamily(value) {
      if (value) {
        this.chain().setFontFamily(value).run();
      } else {
        this.chain().unsetFontFamily().run();
      }
    },
    setFontSize(value) {
      if (value) {
        this.chain().setFontSize(String(value)).run();
      } else {
        this.chain().unsetFontSize().run();
      }
    },
    setLineHeight(value) {
      if (value) {
        this.chain().setLineHeight(String(value)).run();
      } else {
        this.chain().unsetLineHeight().run();
      }
    },
    toggleRtl() {
      const nextDirection = this.isRtlActive ? 'ltr' : 'rtl';
      const align = nextDirection === 'rtl' ? 'right' : 'left';
      this.chain().setTextDirection(nextDirection).setTextAlign(align).run();
    },
    setColor(value) {
      this.chain().setColor(value).run();
    },
    setHighlight(value) {
      this.chain().setHighlight({ color: value }).run();
    },
    toggleLink() {
      const previous = this.editor.getAttributes('link').href;
      const url = window.prompt('Enter URL', previous || 'https://');
      if (url === null) return;
      if (url === '') {
        this.chain().extendMarkRange('link').unsetLink().run();
        return;
      }
      this.chain().extendMarkRange('link').setLink({ href: url }).run();
    },
    addImage() {
      const url = window.prompt('Image URL');
      if (url) {
        this.chain().setImage({ src: url }).run();
      }
    },
    clearFormat() {
      this.chain().unsetAllMarks().clearNodes().run();
    },
    toggleVariableMenu() {
      this.showVariableMenu = !this.showVariableMenu;
      if (this.showVariableMenu) {
        // Hidden until placed, so it never shows for a frame where it last was.
        this.variableMenuStyle = { ...this.variableMenuStyle, visibility: 'hidden' };
        this.$nextTick(this.updateVariableMenuPosition);
      }
    },
    updateVariableMenuPosition() {
      if (!this.showVariableMenu || typeof window === 'undefined') return;
      const button = this.$refs.variableButton;
      const menu = this.$refs.variableMenu;
      if (!button) return;

      const viewportPadding = 8;
      const gap = 6;
      const menuWidth = menu?.offsetWidth || 190;
      const rect = button.getBoundingClientRect();
      const menuHeight = menu?.offsetHeight || 0;
      const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

      const left = Math.min(
        Math.max(viewportPadding, rect.right - menuWidth),
        Math.max(viewportPadding, viewportWidth - menuWidth - viewportPadding)
      );
      let top = rect.bottom + gap;
      if (menuHeight && top + menuHeight > viewportHeight - viewportPadding) {
        top = Math.max(viewportPadding, rect.top - menuHeight - gap);
      }

      this.variableMenuStyle = {
        top: `${top}px`,
        left: `${left}px`,
        visibility: 'visible',
      };
    },
    insertVariable(variable) {
      this.showVariableMenu = false;
      if (this.editor) {
        this.editor.chain().focus().insertContent(`{${variable}}`).run();
      }
    },
    runVariableAction() {
      this.showVariableMenu = false;
      this.$emit('variable-action');
    },
    handleDocumentClick(event) {
      const target = event.target;
      const clickedButton = target?.closest?.('.rte-variable');
      const clickedMenu = target?.closest?.('.rte-variable__menu');
      if (this.showVariableMenu && !clickedButton && !clickedMenu) {
        this.showVariableMenu = false;
      }
    },
    touchToolbarState() {
      this.toolbarStateKey += 1;
    },
  },
  mounted() {
    this.editor = markRaw(
      new Editor({
        // With a template, an empty editor starts from a bare paragraph, which
        // takes the template's alignment and direction.
        content: this.modelValue || (this.slotFormat ? '' : DEFAULT_EDITOR_CONTENT),
        extensions: [
          StarterKit,
          Underline,
          TextStyle,
          FontSize,
          Color,
          FontFamily,
          LineHeight,
          TextDirection,
          Highlight.configure({ multicolor: true }),
          Link.configure({ openOnClick: false, autolink: true }),
          Image,
          TextAlign.configure({ types: ['heading', 'paragraph'] }),
          TemplateSlot.configure({ format: this.slotFormat }),
        ],
        onCreate: () => {
          this.touchToolbarState();
        },
        onUpdate: () => {
          this.$emit('update:modelValue', this.editor.getHTML());
          this.touchToolbarState();
        },
        onSelectionUpdate: () => {
          this.touchToolbarState();
        },
        onFocus: () => {
          this.touchToolbarState();
        },
      })
    );
    document.addEventListener('click', this.handleDocumentClick);
    window.addEventListener('resize', this.updateVariableMenuPosition);
    window.addEventListener('scroll', this.updateVariableMenuPosition, true);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleDocumentClick);
    window.removeEventListener('resize', this.updateVariableMenuPosition);
    window.removeEventListener('scroll', this.updateVariableMenuPosition, true);
    if (this.editor) {
      this.editor.destroy();
      this.editor = null;
    }
  },
};
</script>

<style scoped>
.rte {
  border: 1px solid var(--color-border, #e1e5ec);
  border-radius: 10px;
  background: #ffffff;
  overflow: hidden;
}

.rte--invalid {
  border-color: var(--color-danger, #dc2626);
}

.rte__toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 3px;
  padding: 8px 10px;
  border-bottom: 1px solid #e5e7eb;
  background: #ffffff;
}

.rte__tb-divider {
  width: 1px;
  height: 20px;
  margin: 0 4px;
  background: #e5e7eb;
}

.rte__tb-spacer {
  flex: 1 1 auto;
}

.rte__tb-btn {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
  min-width: 30px;
  height: 30px;
  padding: 0 7px;
  border: 1px solid transparent;
  border-radius: 6px;
  background: transparent;
  color: #374151;
  font-size: 13px;
  line-height: 1;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease;
}

.rte__tb-btn i {
  font-size: 13px;
}

.rte__tb-btn:hover {
  background: #f3f4f6;
}

.rte__tb-btn.is-active {
  border-color: #e0e4f6;
  background: #eef2ff;
  color: #4f46e5;
}

/* Given by the template, not set on the text: shown, but not as "on". */
.rte__tb-btn.is-inherited {
  border-style: dashed;
  border-color: #c7cdf3;
  color: #6366f1;
}

.rte__tb-btn--bold {
  font-weight: 700;
}

.rte__tb-btn--italic {
  font-style: italic;
}

.rte__tb-btn--underline {
  text-decoration: underline;
}

.rte__tb-btn--strike {
  text-decoration: line-through;
}

.rte__tb-btn--text-color {
  flex-direction: column;
  gap: 1px;
  font-weight: 600;
}

.rte__tb-color-bar {
  display: block;
  width: 16px;
  height: 3px;
  border-radius: 2px;
}

.rte__tb-swatch {
  display: block;
  width: 16px;
  height: 16px;
  border: 1px solid rgba(15, 23, 42, 0.15);
  border-radius: 4px;
}

.rte__tb-color-input {
  position: absolute;
  inset: 0;
  width: 100% !important;
  height: 100% !important;
  margin: 0;
  padding: 0;
  border: none !important;
  opacity: 0;
  cursor: pointer;
}

.rte__tb-select {
  height: 30px !important;
  box-sizing: border-box;
  padding: 0 6px;
  border: 1px solid #e0e4eb !important;
  border-radius: 6px !important;
  background: #ffffff;
  color: #374151;
  font-size: 13px !important;
  line-height: 1 !important;
  cursor: pointer;
}

.rte__tb-select option {
  min-height: 24px;
  padding: 2px 6px;
  font-size: 13px;
  line-height: 1.15;
}

.rte__tb-select--small {
  width: 72px;
}

.rte__tb-select--zoom {
  width: 78px;
}

.rte-variable {
  position: relative;
  margin-left: auto;
}

.rte-variable__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 96px;
  height: 30px;
  padding: 0 12px;
  border: 1px solid #e0e4eb;
  border-radius: 8px;
  background: #ffffff;
  color: #111827;
  font-size: 13px;
  font-weight: 500;
  line-height: 1;
  cursor: pointer;
}

.rte-variable__btn i {
  font-size: 10px;
}

.rte-variable__menu {
  position: fixed;
  z-index: 4000;
  width: 190px;
  max-height: calc(100dvh - 16px);
  overflow-y: auto;
  padding: 6px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 12px 24px rgba(15, 23, 42, 0.12);
}

.rte-variable__menu button {
  display: block;
  width: 100%;
  height: 32px;
  padding: 0 10px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #1f2937;
  font-size: 13px;
  text-align: left;
  cursor: pointer;
}

.rte-variable__menu button:hover {
  background: #f5f6f8;
}

.rte-variable__menu--rich {
  width: 252px;
}

.rte-variable__menu .rte-variable__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.rte-variable__label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rte-variable__required {
  flex-shrink: 0;
  color: var(--color-danger, #ef4444);
  font-size: 11.5px;
  font-weight: 500;
}

.rte-variable__divider {
  height: 1px;
  margin: 6px 4px;
  background: #eef0f4;
}

.rte-variable__menu .rte-variable__action {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #4f46e5;
  font-weight: 500;
}

.rte-variable__menu .rte-variable__action:hover {
  background: #eef0ff;
}

.rte-variable__action i {
  font-size: 11px;
}

.rte__canvas {
  padding: 0;
}

/* The editor's type is set here and inherited, so a template's own (drawn on
   .ProseMirror by rich_template_slot_editor) replaces it — and a relative size
   in it scales from this one. */
.rte__editor {
  background: #ffffff;
  color: #111827;
  font-size: 14px;
  line-height: 1.5;
}

.rte__editor :deep(.ProseMirror) {
  min-height: var(--rte-height, 320px);
  padding: 12px 14px;
  outline: none;
}

.rte__editor :deep(.ProseMirror > *:first-child) {
  margin-top: 0;
}

.rte__editor :deep(.ProseMirror p) {
  margin: 0 0 8px;
}

.rte__editor :deep(.ProseMirror ul),
.rte__editor :deep(.ProseMirror ol) {
  margin: 0 0 8px;
  padding-left: 22px;
}

.rte__editor :deep(.ProseMirror a) {
  color: #4f46e5;
  text-decoration: underline;
}

.rte__editor :deep([dir="rtl"]) {
  direction: rtl;
  text-align: right;
}

.rte__editor :deep(.ProseMirror img) {
  max-width: 100%;
  height: auto;
}

/* ---- Written in a template's formatting (slotFormat) ----
   Laid out as the screen lays it out, inside Quill's .ql-editor: paragraphs
   and headings without margins, and a right-to-left paragraph aligned by its
   own setting or the template's rather than pushed right. */
.rte--slot .rte__editor :deep(.ProseMirror :is(p, h1, h2, h3, h4, h5, h6, blockquote, pre)) {
  margin: 0;
}

.rte--slot .rte__editor :deep(.ProseMirror :is(blockquote, pre)) {
  padding: 0;
}

.rte--slot .rte__editor :deep([dir="rtl"]) {
  text-align: inherit;
}

/* A link as the screen draws it: Bootstrap's (public/index.html) colour,
   without an underline. */
.rte--slot .rte__editor :deep(.ProseMirror a) {
  color: #007bff;
  text-decoration: none;
}

/* Lists indented as Quill indents them on the screen: 1.5em for the list and
   1.5em more for the item. */
.rte--slot .rte__editor :deep(.ProseMirror :is(ul, ol)) {
  margin: 0;
  padding-left: 1.5em;
}

.rte--slot .rte__editor :deep(.ProseMirror li) {
  padding-left: 1.5em;
}

/* An item whose line is right to left: Quill's RTL item on the screen, its
   marker and indent on the right. */
.rte--slot .rte__editor :deep(.ProseMirror li:has(> p[dir="rtl"]:first-child)) {
  direction: rtl;
  padding-left: 0;
  padding-right: 1.5em;
}

/* A slot in a list item: the item's marker beside the line, as Quill draws
   it on the screen (li::before). */
.rte__editor :deep(.rte-slot-marker)::before {
  content: var(--rte-slot-marker);
  display: inline-block;
  width: 1.2em;
  margin-left: -1.5em;
  margin-right: 0.3em;
  text-align: right;
  white-space: nowrap;
}

/* ...and above a first block that is not a line, on a line of its own, in the
   item's own type — as the display leaves it. */
.rte__editor :deep(.rte-slot-marker-alone)::before {
  content: var(--rte-slot-marker);
  display: block;
  width: 1.2em;
  margin-left: -1.5em;
  text-align: right;
  white-space: nowrap;
  font-size: var(--rte-slot-item-font-size, 14px);
  font-weight: 400;
  line-height: var(--rte-slot-item-line-height, 1.42);
}

.rte__editor :deep(.rte-slot-marker--rtl)::before {
  margin-left: 0.3em;
  margin-right: -1.5em;
  text-align: inherit;
}

/* An empty line is as tall as a line in the template's font: on the screen it
   holds a line break in that formatting. */
.rte__editor :deep(.rte-slot-empty:not(.rte-slot-marker))::before {
  content: '\200b';
  font-size: var(--rte-slot-font-size, inherit);
  font-family: var(--rte-slot-font-family, inherit);
  line-height: var(--rte-slot-line-height, inherit);
}
</style>
